/**
 * Menus, on the shared explore shell.
 *
 * This page used to declare its own grid and its own card, which is why moving
 * between the explore tabs made the layout jump: the menu card was the only one
 * without a shadow, and its heading was larger than the dish and chef headings for
 * the same role.
 *
 * It now passes content to `ExploreCard` and lets the shared shell own structure,
 * type scale, spacing, motion and the loading / empty / list transition. The only
 * things this page decides are the copy and the destination.
 */

import { useMemo, useState } from 'react'
import { ChefHatIcon } from '../../components/icons'
import {
  ExploreCard,
  ExploreEmpty,
  ExploreGrid,
  ExploreState,
} from '../../components/chow/ExploreCard'
import { FilterPills, PageHeader, Screen, SearchField, type PillOption } from '../../components/chow'
import { styled } from '@linaria/react'
import { minimalVars as v } from '@ovasabi/ui-minimal/tokens'
import { Link } from 'react-router-dom'
import { useChefMenuSnapshot } from '../../generated/prototypeRuntime'
import { enumShort } from './chefShared'
import { useUiDishes, useKitchenNames } from './projections'
import { ChowThumb } from './ChowThumb'
import { ExploreNavigation } from './ExploreNavigation'
import { usePublicMenus } from '../../lib/discover'
import { curatedMenus } from './content'
import { allowSeedContent } from '../../lib/runtime'

type FilterKey = 'all' | 'rice' | 'swallow' | 'grill' | 'chops'

const options: readonly PillOption<FilterKey>[] = [
  { value: 'all', label: 'All' },
  { value: 'rice', label: 'Rice' },
  { value: 'swallow', label: 'Soups & Swallow' },
  { value: 'grill', label: 'Grills & Suya' },
  { value: 'chops', label: 'Small Chops' },
]

const FILTER_PATTERNS: Record<FilterKey, RegExp | null> = {
  all: null,
  rice: /rice|jollof/i,
  swallow: /soup|swallow|comfort/i,
  grill: /grill|suya|roast|bbq/i,
  chops: /chop|platter|snack|finger/i,
}

const matchesMenuFilter = (
  filter: FilterKey,
  title: string,
  items: readonly { category?: string }[],
): boolean => {
  if (filter === 'all') return true
  if (items.some((dish) => dish.category === filter)) return true
  const pattern = FILTER_PATTERNS[filter]
  return pattern ? pattern.test(title) : false
}

/**
 * The way out of an empty menu shelf.
 *
 * An empty state with no route onward strands the customer on a dead end, so the
 * copy and the destination are part of the empty state rather than an afterthought.
 */
const BrowseDishes = styled(Link)`
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  color: ${v.color.brand};
  font-weight: ${v.typography.weightBold};
`

/** Published collections reuse the existing customer-visible dish projection. */
export const MenusPage = () => {
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<FilterKey>('all')
  const menus = useChefMenuSnapshot() as { records: readonly Record<string, unknown>[] }
  // The live feed when it answers, the projection otherwise. As with chefs, the
  // rows come from a synchronous read, so an in-flight feed is not a loading state
  // for this page and must not replace rows it already holds.
  const { menus: liveMenus, loading: liveLoading } = usePublicMenus()
  /**
   * The live feed onto the shape this page reads.
   *
   * `GET /v1/discover/menus` returns published menus and names its columns
   * differently: `cover` rather than `cover_image_url`, `chef_id` rather than
   * `chef_profile_id`, and no status at all. Adapting the columns here is what
   * makes the live lane work. When offline or during demo pre-auth, it falls back
   * to curated menus so layout is stable.
   */
  const source: readonly Record<string, unknown>[] = useMemo(() => {
    if (liveMenus.length > 0) {
      return liveMenus.map((menu) => ({
        ...menu,
        // Published by definition: the endpoint serves published menus only.
        status: 'published',
        cover_image_url: menu.cover ?? '',
        chef_profile_id: menu.chef_id,
      }))
    }
    const realMenus = menus.records.filter((m) => enumShort(m.status) === 'published')
    if (realMenus.length > 0) {
      return realMenus
    }
    if (allowSeedContent()) {
      return curatedMenus
    }
    return []
  }, [liveMenus, menus.records])

  const { records: dishes } = useUiDishes()
  const kitchenName = useKitchenNames()

  const visible = useMemo(() => {
    const search = query.trim().toLowerCase()
    // The public list is the live lane; the projection carries the seeded rows.
    return source.filter((menu) => {
      if (enumShort(menu.status) !== 'published') return false
      const items = dishes.filter((dish) => dish.menuId === String(menu.id))
      const count = items.length > 0 ? items.length : Number(menu.dish_count || 0)
      if (count === 0 && items.length === 0) return false

      const title = String(menu.title || '').toLowerCase()
      const chef = items[0]?.chef
        ? String(items[0].chef).toLowerCase()
        : String(menu.chef || '').toLowerCase()
      const matchesSearch = !search || title.includes(search) || chef.includes(search)
      if (!matchesSearch) return false
      return matchesMenuFilter(filter, title, items)
    })
  }, [source, dishes, query, filter])

  const isInitialLoading = liveLoading && source.length === 0

  return (
    <Screen
      header={
        <PageHeader title="Chef Menus" accentDot>
          <SearchField placeholder="Find a menu or kitchen" value={query} onChange={setQuery} />
        </PageHeader>
      }
    >
      <ExploreNavigation />
      <FilterPills
        ariaLabel="Filter menus"
        value={filter}
        options={options}
        onChange={(val) => setFilter(val)}
      />
      <ExploreState loading={isInitialLoading} skeletonCount={6}>
        {visible.length === 0 ? (
          <ExploreEmpty
            icon={<ChefHatIcon />}
            title={query ? 'No menus match your search' : 'Menus are on their way'}
            description={
              query
                ? 'Try another menu or kitchen name.'
                : 'You can still explore individual dishes or browse a chef’s kitchen.'
            }
            action={<BrowseDishes to="/dishes">Browse dishes →</BrowseDishes>}
          />
        ) : (
          <ExploreGrid>
            {visible.map((menu) => {
              const items = dishes.filter((dish) => dish.menuId === String(menu.id))
              const title = String(menu.title || 'Kitchen menu')
              const count = items.length > 0 ? items.length : Number(menu.dish_count || 0)
              const chefName =
                menu.chef_profile_id && kitchenName(String(menu.chef_profile_id)) !== 'Kitchen'
                  ? kitchenName(String(menu.chef_profile_id))
                  : String(menu.chef || kitchenName(String(menu.chef_profile_id || '')))
              return (
                <ExploreCard
                  key={String(menu.id)}
                  to={`/dishes?menu=${encodeURIComponent(String(menu.id))}`}
                  media={
                    <ChowThumb
                      src={String(menu.cover_image_url || items[0]?.image || '')}
                      alt={title}
                      name={title}
                    />
                  }
                  title={title}
                  subtitle={chefName}
                  meta={`${count} ${count === 1 ? 'dish' : 'dishes'}`}
                  trailing="Explore menu →"
                />
              )
            })}
          </ExploreGrid>
        )}
      </ExploreState>
    </Screen>
  )
}
